import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { appDialog } from '../utils/appDialog';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import { Trophy, Users, DollarSign, Calendar, ArrowRight, Ticket, CheckCircle, XCircle, Upload, X, Lock, Vote, ImagePlus } from 'lucide-react';
import { describeContestAvailability } from '../utils/contestAvailability';
import { environment, routes } from '../config/environment';
import { useSeoMetadata } from '../hooks/useSeoMetadata';
import { getCompleteImagePath } from '../utils/imageUtils';
import petflixAuthService from '../services/petflixAuthService';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';
import {
  formatMediaRulesSummary,
  getMediaFileAcceptValue,
  getParticipantMediaLabel,
  validateMediaFile,
  validateMediaUrlForContest,
  type ContestMediaRules,
} from '../utils/mediaStandards';
import { uploadParticipantMedia } from '../services/s3UploadService';
import DownloadAppCard from '../components/DownloadAppCard';
import BilingualLabel from '../components/BilingualLabel';

interface Contest {
  id: string;
  name: string;
  description: string;
  entrance_fee: number;
  min_participants: number;
  max_participants: number;
  current_participants: number;
  status: string;
  phase: string;
  contest_state?: string;
  submission_start?: string;
  submission_deadline: string;
  voting_start: string;
  voting_end: string;
  winner_announcement?: string;
  sponsor_name?: string;
  logo_url?: string;
}

interface JoinDialogData {
  contestId: string;
  contestName: string;
  entranceFee: number;
  currentParticipants: number;
  minParticipants: number;
  maxParticipants: number;
  mediaRules: ContestMediaRules;
}

type EntryType = 'normal' | 'coupon' | 'petflix' | null;

interface CouponValidation {
  valid: boolean;
  code: string;
  uses_remaining: number;
  max_uses: number;
  coupon_pass_name?: string;
  // 'coupon' = código de cupón (CouponPass_CouponCodes); 'pass' = boleto de
  // Contest Pass (Pass_Codes). El QR impreso puede llevar cualquiera de los dos.
  kind?: 'coupon' | 'pass';
  error?: string;
}

const ContestParticipantPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedContestId = searchParams.get('contestId') || '';
  
  const [contests, setContests] = useState<Contest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joinDialogData, setJoinDialogData] = useState<JoinDialogData | null>(null);
  const [joining, setJoining] = useState(false);
  const [joinFormData, setJoinFormData] = useState({
    participant_name: '',
    participant_email: '',
    participant_phone: '',
    payment_method: '',
    coupon_code: '',
    photo_url: ''
  });
  const [petflixFormData, setPetflixFormData] = useState({
    user_id: '',
    post_social_id: '',
    coupon_code: ''
  });
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponValidation, setCouponValidation] = useState<CouponValidation | null>(null);
  const [selectedEntryType, setSelectedEntryType] = useState<EntryType>(null);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [mediaPreviewType, setMediaPreviewType] = useState<'image' | 'video' | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para el formulario join-by-name
  const [joinByNameDialogOpen, setJoinByNameDialogOpen] = useState(false);
  const [joinByNameFormData, setJoinByNameFormData] = useState({
    contest_name: '',
    user_id: '',
    post_social_id: '',
    coupon_code: '',
    access_key: ''
  });
  const [joiningByName, setJoiningByName] = useState(false);
  const [joinByNameError, setJoinByNameError] = useState<string>('');
  const [joinByNameSuccess, setJoinByNameSuccess] = useState<string>('');

  // Tabs para separar concursos disponibles / no disponibles y no saturar la página
  const [availabilityTab, setAvailabilityTab] = useState<'available' | 'unavailable' | 'all'>('available');

  // Aplicar SEO metadata desde la base de datos (valores globales)
  useSeoMetadata({
    useGlobalSeo: true
  });

  // Cargar todos los concursos
  useEffect(() => {
    fetchContests();
  }, []);

  // Entrada por link de ticket digital: /contest_participant?contestId=&code=
  // Abre automáticamente el join del concurso con tipo "coupon" y el código
  // precargado y validado (entrada gratis).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contestId = params.get('contestId');
    const code = params.get('code');
    if (!contestId || !code) return;
    void autoOpenCouponJoin(contestId, code.trim());
  }, []);

  // Valida un código de redención que puede ser de CUPÓN (C) o de PASE (P).
  // Intenta primero como cupón; si el cupón "no existe", reintenta como pase.
  // Así un mismo QR/boleto impreso funciona sin importar el tipo de pase.
  const validateRedemptionCode = async (code: string, contestId: string): Promise<CouponValidation> => {
    const trimmed = code.trim();
    // 1) Intentar como CUPÓN
    let couponNotFound = false;
    try {
      const res = await fetch(`${environment.apiBaseUrl}/couponpasses/validate`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ code: trimmed, contestId })
      });
      const result = await res.json();
      if (result.success && result.valid) {
        return {
          valid: true,
          kind: 'coupon',
          code: result.coupon_code.code,
          uses_remaining: result.coupon_code.uses_remaining,
          max_uses: result.coupon_code.max_uses,
          coupon_pass_name: result.coupon_pass?.name
        };
      }
      // Solo caemos a "pase" si el cupón NO existe; si existe pero es inválido
      // (sin usos, inactivo, otro concurso) conservamos ese error específico.
      couponNotFound = res.status === 404 || /not found/i.test(result?.error || '');
      if (!couponNotFound) {
        return { valid: false, kind: 'coupon', code: trimmed, uses_remaining: 0, max_uses: 0, error: result.error || 'Código de cupón inválido' };
      }
    } catch (_) {
      couponNotFound = true; // si falla la red del cupón, igual probamos pase
    }

    // 2) Intentar como PASE (boleto de Contest Pass)
    try {
      const res = await fetch(`${environment.apiBaseUrl}/passcodes/validate`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ code: trimmed, contestId })
      });
      const result = await res.json();
      if (result.success && result.valid) {
        return {
          valid: true,
          kind: 'pass',
          code: result.pass_code.code,
          uses_remaining: result.pass_code.uses_remaining,
          max_uses: result.pass_code.max_uses
        };
      }
      return { valid: false, kind: 'pass', code: trimmed, uses_remaining: 0, max_uses: 0, error: result.error || 'Código no válido' };
    } catch (err: any) {
      return { valid: false, code: trimmed, uses_remaining: 0, max_uses: 0, error: err?.message || 'Error al validar el código' };
    }
  };

  const autoOpenCouponJoin = async (contestId: string, code: string) => {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/join-info`, {
        headers: getApiHeadersForGet()
      });
      if (!response.ok) throw new Error('Error al obtener información del concurso');
      const data = await response.json();
      if (!data.success || !data.contest) {
        void appDialog.alert('No se pudo cargar el concurso de este pase.');
        return;
      }

      setJoinDialogData({
        contestId: data.contest.id,
        contestName: data.contest.name,
        entranceFee: data.contest.entrance_fee || 0,
        currentParticipants: data.contest.current_participants || data.contest.participant_count || 0,
        minParticipants: data.contest.min_participants || 0,
        maxParticipants: data.contest.max_participants || data.contest.min_participants || 0,
        mediaRules: {
          allowed_media_types: data.contest.allowed_media_types,
          allowed_formats: data.contest.allowed_formats,
          max_images: data.contest.max_images,
          max_videos: data.contest.max_videos,
          max_image_size_mb: data.contest.max_image_size_mb,
          max_video_size_mb: data.contest.max_video_size_mb,
          max_video_duration_seconds: data.contest.max_video_duration_seconds,
        },
      });
      setJoinFormData({
        participant_name: '',
        participant_email: '',
        participant_phone: '',
        payment_method: '',
        coupon_code: code,
        photo_url: ''
      });
      setCouponValidation(null);
      setSelectedEntryType('coupon');
      clearSelectedMedia();
      setJoinDialogOpen(true);

      // Validar el código (cupón o pase) con el contexto conocido.
      const validation = await validateRedemptionCode(code, data.contest.id);
      setCouponValidation(validation);
    } catch (err: any) {
      console.error('Error auto-opening coupon join:', err);
      void appDialog.alert(err.message || 'No se pudo abrir el concurso de este pase.');
    }
  };

  useEffect(() => {
    return () => {
      if (mediaPreviewUrl) {
        URL.revokeObjectURL(mediaPreviewUrl);
      }
    };
  }, [mediaPreviewUrl]);

  const clearSelectedMedia = () => {
    if (mediaPreviewUrl) {
      URL.revokeObjectURL(mediaPreviewUrl);
    }
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setMediaPreviewType(null);
    setJoinFormData((prev) => ({ ...prev, photo_url: '' }));
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  };

  const handleMediaFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !joinDialogData?.mediaRules) {
      return;
    }

    const validation = validateMediaFile(file, joinDialogData.mediaRules);
    if (!validation.valid) {
      void appDialog.alert(validation.error || 'Archivo no válido para este concurso');
      event.target.value = '';
      return;
    }

    if (mediaPreviewUrl) {
      URL.revokeObjectURL(mediaPreviewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setMediaFile(file);
    setMediaPreviewUrl(previewUrl);
    setMediaPreviewType(file.type.startsWith('video/') ? 'video' : 'image');
    setJoinFormData((prev) => ({ ...prev, photo_url: '' }));
  };

  const fetchContests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${environment.apiBaseUrl}/contests`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los concursos');
      }
      
      const data = await response.json();
      // Manejar respuesta paginada o no paginada
      const contestsList = Array.isArray(data) ? data : (data.data || []);
      
      // Debug: Verificar datos de patrocinio
      console.log('Contests data from API:', contestsList.map((c: any) => ({
        id: c.id,
        name: c.name,
        sponsor_name: c.sponsor_name,
        logo_url: c.logo_url,
        sponsorship: c.sponsorship
      })));
      
      // Obtener el número real de participantes para cada concurso.
      const contestsWithParticipants = await Promise.all(
        contestsList.map(async (contest: Contest) => {
          const enriched: Contest = { ...contest };
          try {
            const countResponse = await fetch(`${environment.apiBaseUrl}/participants/count/${contest.id}`, {
              headers: getApiHeadersForGet()
            });
            if (countResponse.ok) {
              const countData = await countResponse.json();
              enriched.current_participants = countData.participantCount || 0;
            }
          } catch (err) {
            console.error(`Error fetching participant count for contest ${contest.id}:`, err);
          }
          return enriched;
        })
      );
      
      setContests(contestsWithParticipants);
    } catch (err: any) {
      console.error('Error fetching contests:', err);
      setError(err.message || 'Error al cargar los concursos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenJoinDialog = async (contestId: string) => {
    // Verificación defensiva: si el concurso ya no está disponible según sus
    // fechas/estado, no abrimos el diálogo de inscripción y explicamos por qué.
    const targetContest = contests.find((c) => c.id === contestId);
    if (targetContest) {
      const availability = describeContestAvailability(targetContest, formatDate);
      if (!availability.available) {
        void appDialog.alert(`No es posible unirse a este concurso.\n\n${availability.message}`);
        return;
      }
    }
    try {
      // Obtener información del concurso
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/join-info`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener información del concurso');
      }
      
      const data = await response.json();
      
      if (data.success && data.contest) {
        setJoinDialogData({
          contestId: data.contest.id,
          contestName: data.contest.name,
          entranceFee: data.contest.entrance_fee || 0,
          currentParticipants: data.contest.current_participants || data.contest.participant_count || 0,
          minParticipants: data.contest.min_participants || 0,
          maxParticipants: data.contest.max_participants || data.contest.min_participants || 0,
          mediaRules: {
            allowed_media_types: data.contest.allowed_media_types,
            allowed_formats: data.contest.allowed_formats,
            max_images: data.contest.max_images,
            max_videos: data.contest.max_videos,
            max_image_size_mb: data.contest.max_image_size_mb,
            max_video_size_mb: data.contest.max_video_size_mb,
            max_video_duration_seconds: data.contest.max_video_duration_seconds,
          },
        });
        setJoinFormData({
          participant_name: '',
          participant_email: '',
          participant_phone: '',
          payment_method: '',
          coupon_code: '',
          photo_url: ''
        });
        setPetflixFormData({
          user_id: '',
          post_social_id: '',
          coupon_code: ''
        });
        setCouponValidation(null);
        setSelectedEntryType(null);
        clearSelectedMedia();
        setJoinDialogOpen(true);
      } else {
        void appDialog.alert('Error al obtener información del concurso');
      }
    } catch (err: any) {
      console.error('Error opening join dialog:', err);
      void appDialog.alert(err.message || 'Error al obtener información del concurso');
    }
  };

  const handleValidateCoupon = async () => {
    if (!joinDialogData || !joinFormData.coupon_code.trim()) {
      return;
    }

    try {
      setValidatingCoupon(true);
      setCouponValidation(null);
      const validation = await validateRedemptionCode(joinFormData.coupon_code.trim(), joinDialogData.contestId);
      setCouponValidation(validation);
    } catch (err: any) {
      console.error('Error validating code:', err);
      setCouponValidation({
        valid: false,
        code: joinFormData.coupon_code.trim(),
        uses_remaining: 0,
        max_uses: 0,
        error: err.message || 'Error al validar el código'
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleJoinContest = async () => {
    if (!joinDialogData) return;
    
    // Si es PetFlix, usar la API join-by-name
    if (selectedEntryType === 'petflix') {
      if (!petflixFormData.user_id.trim()) {
        void appDialog.alert('Por favor, ingresa el ID de usuario de PetFlix');
        return;
      }
      if (!petflixFormData.post_social_id.trim()) {
        void appDialog.alert('Por favor, ingresa el ID del post social');
        return;
      }

      try {
        setJoining(true);
        
        const response = await fetch(`${environment.apiBaseUrl}/contests/join-by-name`, {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({
            contest_name: joinDialogData.contestName,
            user_id: petflixFormData.user_id.trim(),
            post_social_id: petflixFormData.post_social_id.trim(),
            coupon_code: petflixFormData.coupon_code.trim() || undefined
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || result.message || 'Error al unirse al concurso');
        }

        if (result.success) {
          const participantMessage = result.participant_creation 
            ? `\n\nParticipante creado: ${result.participant_creation.participantName || 'N/A'}`
            : '\n\nNota: Los datos se guardaron. El participante se procesará automáticamente cuando el post esté disponible.';
          void appDialog.alert(`¡Te has unido exitosamente al concurso "${joinDialogData.contestName}"!${participantMessage}`);
          setJoinDialogOpen(false);
          setJoinDialogData(null);
          setPetflixFormData({
            user_id: '',
            post_social_id: '',
            coupon_code: ''
          });
          setSelectedEntryType(null);
          // Recargar concursos para actualizar el contador de participantes
          fetchContests();
        } else {
          throw new Error(result.error || 'Error al unirse al concurso');
        }
      } catch (err: any) {
        console.error('Error joining contest with PetFlix:', err);
        void appDialog.alert(`Error: ${err.message || 'Error al unirse al concurso'}`);
      } finally {
        setJoining(false);
      }
      return;
    }
    
    // Flujo normal para entrada normal o coupon
    if (!joinFormData.participant_name.trim()) {
      void appDialog.alert('Por favor, ingresa tu nombre');
      return;
    }

    // Si hay un código de cupón, debe estar validado
    if (joinFormData.coupon_code.trim() && (!couponValidation || !couponValidation.valid)) {
      void appDialog.alert('Por favor, valida el código de cupón antes de continuar');
      return;
    }

    if (joinFormData.coupon_code.trim() && (!couponValidation || !couponValidation.valid)) {
      void appDialog.alert('Por favor, valida el código de cupón antes de continuar');
      return;
    }

    let photoUrl = joinFormData.photo_url.trim();

    if (mediaFile && joinDialogData.mediaRules) {
      const fileValidation = validateMediaFile(mediaFile, joinDialogData.mediaRules);
      if (!fileValidation.valid) {
        void appDialog.alert(fileValidation.error || 'El archivo no cumple las reglas del concurso');
        return;
      }

      try {
        setUploadingMedia(true);
        const uploadResult = await uploadParticipantMedia(mediaFile, {
          max_image_size_mb: joinDialogData.mediaRules.max_image_size_mb,
          max_video_size_mb: joinDialogData.mediaRules.max_video_size_mb,
        });
        photoUrl = uploadResult.contentUrl;
      } catch (uploadErr: any) {
        void appDialog.alert(uploadErr.message || 'Error al subir el archivo. Intenta de nuevo.');
        return;
      } finally {
        setUploadingMedia(false);
      }
    } else if (photoUrl && joinDialogData.mediaRules) {
      const mediaValidation = validateMediaUrlForContest(photoUrl, joinDialogData.mediaRules);
      if (!mediaValidation.valid) {
        void appDialog.alert(mediaValidation.error || 'El archivo multimedia no cumple las reglas del concurso');
        return;
      }
    }

    try {
      setJoining(true);
      
      const requestBody: any = {
        participant_name: joinFormData.participant_name,
        participant_email: joinFormData.participant_email || undefined,
        participant_phone: joinFormData.participant_phone || undefined,
        payment_method: joinFormData.payment_method || undefined,
        photo_url: photoUrl || undefined
      };

      // Incluir el código validado en el campo correcto según su tipo.
      if (couponValidation && couponValidation.valid) {
        if (couponValidation.kind === 'pass') {
          requestBody.pass_code = couponValidation.code;
        } else {
          requestBody.coupon_code = couponValidation.code;
        }
      }

      const response = await fetch(`${environment.apiBaseUrl}/contests/${joinDialogData.contestId}/join`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al unirse al concurso');
      }

      const result = await response.json();
      
      if (result.success) {
        const redemption = result.coupon_code_used || result.pass_code_used;
        const usesRemaining = redemption?.uses_remaining ?? (
          couponValidation?.valid
            ? Math.max(0, (couponValidation.uses_remaining ?? 0) - 1)
            : undefined
        );
        const couponMessage = redemption
          ? `\n\n${result.pass_code_used ? 'Pase' : 'Cupón'} aplicado: ${redemption.code} (${usesRemaining ?? 0} usos restantes)`
          : '';
        void appDialog.alert(`¡Te has unido exitosamente al concurso "${result.contest.name}"!\n\nParticipante: ${result.participant.participant_name}${couponMessage}`);
        setJoinDialogOpen(false);
        setJoinDialogData(null);
        setJoinFormData({
          participant_name: '',
          participant_email: '',
          participant_phone: '',
          payment_method: '',
          coupon_code: '',
          photo_url: ''
        });
        setCouponValidation(null);
        setSelectedEntryType(null);
        clearSelectedMedia();
        // Recargar concursos para actualizar el contador de participantes
        fetchContests();
      } else {
        throw new Error(result.error || 'Error al unirse al concurso');
      }
    } catch (err: any) {
      console.error('Error joining contest:', err);
      void appDialog.alert(`Error: ${err.message || 'Error al unirse al concurso'}`);
    } finally {
      setJoining(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleOpenJoinByNameDialog = () => {
    setJoinByNameFormData({
      contest_name: '',
      user_id: '',
      post_social_id: '',
      coupon_code: '',
      access_key: ''
    });
    setJoinByNameError('');
    setJoinByNameSuccess('');
    setJoinByNameDialogOpen(true);
  };

  const handleJoinByName = async () => {
    // Validar campos requeridos
    if (!joinByNameFormData.contest_name.trim()) {
      setJoinByNameError('El nombre del concurso es requerido');
      return;
    }
    if (!joinByNameFormData.user_id.trim()) {
      setJoinByNameError('El ID de usuario es requerido');
      return;
    }
    if (!joinByNameFormData.post_social_id.trim()) {
      setJoinByNameError('El ID del post social es requerido');
      return;
    }

    try {
      setJoiningByName(true);
      setJoinByNameError('');
      setJoinByNameSuccess('');

      // Obtener PET_CONTEST_ACCESS_KEY: primero del campo del formulario, luego del entorno
      const petContestAccessKey = joinByNameFormData.access_key.trim() || environment.petContestAccessKey;
      
      if (!petContestAccessKey) {
        throw new Error('PET_CONTEST_ACCESS_KEY es requerida. Ingrese la clave en el campo "Clave de Autenticación" o configure REACT_APP_PET_CONTEST_ACCESS_KEY en el archivo .env');
      }

      const requestBody: any = {
        contest_name: joinByNameFormData.contest_name.trim(),
        user_id: joinByNameFormData.user_id.trim(),
        post_social_id: joinByNameFormData.post_social_id.trim(),
        coupon_code: joinByNameFormData.coupon_code.trim() || undefined
      };

      const response = await fetch(`${environment.apiBaseUrl}/contests/join-by-name`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Error al unirse al concurso');
      }

      if (result.success) {
        setJoinByNameSuccess(`¡Datos guardados exitosamente! Participante ID: ${result.participant.id}`);
        // Limpiar formulario después de 3 segundos
        setTimeout(() => {
          setJoinByNameFormData({
            contest_name: '',
            user_id: '',
            post_social_id: '',
            coupon_code: '',
            access_key: ''
          });
          setJoinByNameSuccess('');
        }, 3000);
      } else {
        throw new Error(result.error || 'Error al guardar los datos');
      }
    } catch (err: any) {
      console.error('Error joining contest by name:', err);
      setJoinByNameError(err.message || 'Error al unirse al concurso');
    } finally {
      setJoiningByName(false);
    }
  };

  const participantSidebarSx = {
    width: { xs: '100%', lg: 340 },
    maxWidth: { xs: '100%', lg: 340 },
    flexShrink: 0,
    alignSelf: 'flex-start',
  } as const;

  const participantContentSx = {
    flex: 1,
    minWidth: 0,
    width: '100%',
  } as const;

  const participantContentInnerSx = {
    width: '100%',
    maxWidth: 980,
    mx: 'auto',
    textAlign: 'center',
  } as const;

  const pageHeader = (
    <Box sx={{ mb: { xs: 3, md: 4 } }}>
      <Box className="napc-eyebrow" sx={{ mx: 'auto' }}>
        Land & install
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#111827', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
        Concursos Disponibles
      </Typography>
      <Typography variant="body1" sx={{ color: '#6B6F7B', mb: 1.5, fontWeight: 600 }}>
        Available Contests
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: '#6B6F7B',
          maxWidth: 560,
          mx: 'auto',
          lineHeight: 1.55,
          px: { xs: 1, sm: 0 },
        }}
      >
        Explora concursos de mascotas con una experiencia cálida, clara y lista para mobile.
        <Box component="span" sx={{ display: 'block', color: '#9ca3af', mt: 0.5 }}>
          Explore pet contests with a warm, clear experience ready for mobile.
        </Box>
      </Typography>
      <Button
        variant="text"
        onClick={handleOpenJoinByNameDialog}
        sx={{
          color: '#B01F8E',
          textTransform: 'none',
          fontSize: '0.8rem',
          mt: 1,
          opacity: 0.85,
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
            opacity: 1,
          }
        }}
      >
        Unirse por nombre de concurso
      </Button>
    </Box>
  );

  return (
    <Box className="napc-page" sx={{ minHeight: '100vh', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 3, lg: 4 },
            alignItems: 'flex-start',
          }}
        >
          <Box component="aside" aria-label="Download Noah's Ark app" sx={participantSidebarSx}>
            <DownloadAppCard />
          </Box>

          <Box component="main" sx={participantContentSx}>
          <Box sx={participantContentInnerSx}>
        {pageHeader}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ py: 4 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#6b7280' }}>
              Cargando concursos... / Loading contests...
            </Typography>
          </Box>
        )}

        {/* Contests Grid */}
        {!loading && contests.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Trophy className="w-16 h-16 mx-auto mb-4" style={{ color: '#d1d5db' }} />
            <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
              No hay concursos disponibles
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mb: 0.5 }}>
              Vuelve más tarde para ver nuevos concursos
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              Check back later for new contests
            </Typography>
          </Box>
        )}

        {/* Tabs + concursos: sidebar app a la izquierda, contenido a la derecha */}
        {!loading && contests.length > 0 && (() => {
          const availableCount = contests.filter((c) => describeContestAvailability(c, formatDate).available).length;
          const unavailableCount = contests.length - availableCount;
          const visibleContests = contests.filter((c) => {
            const available = describeContestAvailability(c, formatDate).available;
            if (availabilityTab === 'available') return available;
            if (availabilityTab === 'unavailable') return !available;
            return true;
          });

          const tabsBar = (
            <Box sx={{ mb: 3, borderBottom: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tabs
                value={availabilityTab}
                onChange={(_e, value) => setAvailabilityTab(value)}
                centered
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  width: '100%',
                  maxWidth: 720,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 44,
                    fontSize: { xs: '0.72rem', sm: '0.82rem' },
                    px: { xs: 1, sm: 1.5 },
                    minWidth: { xs: 'auto', sm: 140 },
                  },
                  '& .Mui-selected': { color: '#ED1790' },
                  '& .MuiTabs-indicator': { backgroundColor: '#ED1790' },
                }}
              >
                <Tab
                  value="available"
                  icon={<CheckCircle className="w-4 h-4" />}
                  iconPosition="start"
                  label={`Disponibles (${availableCount})`}
                />
                <Tab
                  value="unavailable"
                  icon={<Lock className="w-4 h-4" />}
                  iconPosition="start"
                  label={`No disponibles (${unavailableCount})`}
                />
                <Tab value="all" label={`Todos (${contests.length})`} />
              </Tabs>
              <Typography variant="caption" sx={{ display: 'block', color: '#9ca3af', pb: 1, px: 0.5 }}>
                Available · Unavailable · All
              </Typography>
            </Box>
          );

          if (visibleContests.length === 0) {
            return (
              <>
                {tabsBar}
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  {availabilityTab === 'available' ? (
                    <>
                      <Lock className="w-14 h-14 mx-auto mb-3" style={{ color: '#d1d5db' }} />
                      <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                        No hay concursos disponibles en este momento
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                        Revisa &quot;No disponibles&quot; o vuelve más tarde.
                        <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                          Check &quot;Unavailable&quot; or come back later.
                        </Box>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Trophy className="w-14 h-14 mx-auto mb-3" style={{ color: '#d1d5db' }} />
                      <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                        No hay concursos en esta categoría
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                        No contests in this category
                      </Typography>
                    </>
                  )}
                </Box>
              </>
            );
          }

          return (
          <>
            {tabsBar}
            <Grid container spacing={2.5} justifyContent="center">
            {visibleContests.map((contest) => {
              const availability = describeContestAvailability(contest, formatDate);
              return (
              <Grid size={{ xs: 12, md: 6, xl: 4 }} key={contest.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    opacity: availability.available ? 1 : 0.85,
                    ...(availability.available ? {} : { border: '1px solid #fecaca' }),
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Availability Badge: explica claramente si se puede unir o no y por qué */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        p: 1.25,
                        borderRadius: 1,
                        backgroundColor: availability.available ? '#ecfdf5' : '#fef2f2',
                        border: `1px solid ${availability.available ? '#a7f3d0' : '#fecaca'}`,
                      }}
                    >
                      {availability.available ? (
                        <CheckCircle className="w-4 h-4" style={{ color: '#059669', flexShrink: 0 }} />
                      ) : (
                        <Lock className="w-4 h-4" style={{ color: '#dc2626', flexShrink: 0 }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: availability.available ? '#065f46' : '#991b1b',
                          fontWeight: 600,
                          lineHeight: 1.3,
                        }}
                      >
                        {availability.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: availability.available ? '#047857' : '#b91c1c',
                          fontWeight: 500,
                          lineHeight: 1.25,
                          display: 'block',
                          mt: 0.25,
                          opacity: 0.9,
                        }}
                      >
                        {availability.messageEn}
                      </Typography>
                    </Box>

                    {/* Status Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip 
                        label={contest.status || 'N/A'} 
                        color={getStatusColor(contest.status) as any}
                        size="small"
                      />
                      <Chip 
                        label={contest.phase || 'N/A'} 
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    {/* Sponsor Logo and Name */}
                    {(contest.logo_url && contest.logo_url.trim() !== '' || contest.sponsor_name) && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5, 
                        mb: 2,
                        p: 1.5,
                        backgroundColor: '#f3f4f6',
                        borderRadius: 1
                      }}>
                        {contest.logo_url && contest.logo_url.trim() !== '' && (() => {
                          const logoUrl = getCompleteImagePath(contest.logo_url) || contest.logo_url;
                          console.log('Rendering sponsor logo:', {
                            original: contest.logo_url,
                            processed: logoUrl,
                            sponsor_name: contest.sponsor_name
                          });
                          return (
                            <Box
                              component="img"
                              src={logoUrl}
                              alt={contest.sponsor_name || 'Sponsor logo'}
                              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                const target = e.target as HTMLImageElement;
                                console.error('Error loading sponsor logo:', logoUrl);
                                target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('Sponsor logo loaded successfully:', logoUrl);
                              }}
                              sx={{
                                width: 48,
                                height: 48,
                                objectFit: 'contain',
                                borderRadius: 1,
                                backgroundColor: 'white',
                                p: 0.5,
                                border: '1px solid #e5e7eb',
                                flexShrink: 0
                              }}
                            />
                          );
                        })()}
                        {contest.sponsor_name && (
                          <Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#374151',
                                fontWeight: 'medium',
                                flexGrow: 1
                              }}
                            >
                              Patrocinado por: <strong>{contest.sponsor_name}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Sponsored by: {contest.sponsor_name}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Contest Name */}
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#111827', mb: 1.5 }}>
                      {contest.name || 'Sin nombre'}
                    </Typography>

                    {/* Description */}
                    {contest.description && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#6b7280', 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {contest.description}
                      </Typography>
                    )}

                    {/* Contest Info */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <DollarSign className="w-4 h-4" style={{ color: '#059669', marginTop: 2 }} />
                        <Box>
                          <BilingualLabel es="Precio de entrada" en="Entry fee" bold />
                          <Typography variant="body2" sx={{ color: '#374151', mt: 0.25 }}>
                            ${(contest.entrance_fee || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Users className="w-4 h-4" style={{ color: '#2563eb', marginTop: 2 }} />
                        <Box>
                          <BilingualLabel es="Participantes" en="Participants" bold />
                          <Typography variant="body2" sx={{ color: '#374151', mt: 0.25 }}>
                            {contest.current_participants || 0}/{contest.max_participants || contest.min_participants || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>

                      {contest.submission_deadline && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Calendar className="w-4 h-4" style={{ color: '#9333ea', marginTop: 2 }} />
                          <Box>
                            <BilingualLabel es="Cierre" en="Deadline" bold />
                            <Typography variant="body2" sx={{ color: '#374151', mt: 0.25 }}>
                              {formatDate(contest.submission_deadline)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </CardContent>

                  {/* Actions: separate Vote and Participate buttons */}
                  <Box sx={{ p: 2, pt: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={!availability.available && availability.reasonKey !== 'VotingOpen'}
                      startIcon={availability.available || availability.reasonKey === 'VotingOpen' ? <Vote className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      onClick={() => navigate(`${routes.contestVote}?contestId=${encodeURIComponent(contest.id)}`)}
                      sx={{
                        backgroundColor: availability.available || availability.reasonKey === 'VotingOpen' ? '#2563eb' : '#e5e7eb',
                        color: availability.available || availability.reasonKey === 'VotingOpen' ? 'white' : '#9ca3af',
                        fontWeight: 'bold',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#1d4ed8'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#e5e7eb',
                          color: '#9ca3af'
                        }
                      }}
                    >
                      <Box sx={{ textAlign: 'center', lineHeight: 1.1 }}>
                        <Box component="span" sx={{ display: 'block' }}>Votar</Box>
                        <Box component="span" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, opacity: 0.9 }}>Vote</Box>
                      </Box>
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={!availability.available}
                      startIcon={!availability.available ? <Lock className="w-4 h-4" /> : <ImagePlus className="w-4 h-4" />}
                      onClick={() => handleOpenJoinDialog(contest.id)}
                      sx={{
                        backgroundColor: '#ED1790',
                        color: 'white',
                        fontWeight: 'bold',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#B01F8E'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#e5e7eb',
                          color: '#9ca3af'
                        }
                      }}
                    >
                      <Box sx={{ textAlign: 'center', lineHeight: 1.1 }}>
                        <Box component="span" sx={{ display: 'block' }}>Participar</Box>
                        <Box component="span" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, opacity: 0.9 }}>Join</Box>
                      </Box>
                    </Button>
                  </Box>
                </Card>
              </Grid>
              );
            })}
            </Grid>
          </>
          );
        })()}

          </Box>
          </Box>
        </Box>

        {/* Join Dialog */}
        <Dialog 
          open={joinDialogOpen} 
          onClose={() => {
            if (!joining && !uploadingMedia) {
              setJoinDialogOpen(false);
              setSelectedEntryType(null);
              setCouponValidation(null);
              clearSelectedMedia();
              setPetflixFormData({
                user_id: '',
                post_social_id: '',
                coupon_code: ''
              });
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Trophy className="w-5 h-5" style={{ color: '#ED1790' }} />
              Unirse al Concurso
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {joinDialogData && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 1 }}>
                  {joinDialogData.contestName}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                  Participantes: <strong>
                    {joinDialogData.currentParticipants}/{joinDialogData.maxParticipants > 0 ? joinDialogData.maxParticipants : joinDialogData.minParticipants}
                  </strong>
                </Typography>

                {/* Selección de tipo de entrada */}
                {!selectedEntryType && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#111827', mb: 2 }}>
                      Selecciona el tipo de entrada:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setSelectedEntryType('normal')}
                        sx={{
                          py: 2,
                          borderColor: '#ED1790',
                          color: '#B01F8E',
                          '&:hover': {
                            borderColor: '#B01F8E',
                            backgroundColor: 'rgba(237, 23, 144, .06)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <DollarSign className="w-6 h-6" />
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Entrada Normal
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            ${joinDialogData.entranceFee.toFixed(2)}
                          </Typography>
                        </Box>
                      </Button>
                      
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setSelectedEntryType('coupon')}
                        sx={{
                          py: 2,
                          borderColor: '#9333ea',
                          color: '#9333ea',
                          '&:hover': {
                            borderColor: '#7e22ce',
                            backgroundColor: '#f3e8ff'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Ticket className="w-6 h-6" />
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            CouponPass
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            Gratis con código válido
                          </Typography>
                        </Box>
                      </Button>
                      
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          setSelectedEntryType('petflix');
                          setPetflixFormData({
                            user_id: '',
                            post_social_id: '',
                            coupon_code: ''
                          });
                        }}
                        sx={{
                          py: 2,
                          borderColor: '#10b981',
                          color: '#10b981',
                          '&:hover': {
                            borderColor: '#059669',
                            backgroundColor: '#ecfdf5'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Users className="w-6 h-6" />
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            PetFlix
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            Participar con cuenta PetFlix
                          </Typography>
                        </Box>
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Formulario según el tipo de entrada seleccionado */}
                {selectedEntryType && (
                  <Box sx={{ mt: 2 }}>
                    {selectedEntryType === 'normal' && (
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                        Precio de entrada: <strong>${joinDialogData.entranceFee.toFixed(2)}</strong>
                      </Typography>
                    )}

                    {joinDialogData.mediaRules && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Requisitos multimedia
                        </Typography>
                        <Typography variant="caption">
                          {formatMediaRulesSummary(joinDialogData.mediaRules)}
                        </Typography>
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Nombre Completo"
                        required
                        fullWidth
                        value={joinFormData.participant_name}
                        onChange={(e) => setJoinFormData({ ...joinFormData, participant_name: e.target.value })}
                        disabled={joining}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#2563eb',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#2563eb',
                            },
                          },
                        }}
                      />
                      
                      <TextField
                        label="Correo Electrónico"
                        type="email"
                        fullWidth
                        value={joinFormData.participant_email}
                        onChange={(e) => setJoinFormData({ ...joinFormData, participant_email: e.target.value })}
                        disabled={joining}
                        helperText="Opcional si registraste tu teléfono en la app"
                      />
                      
                      <TextField
                        label="Teléfono"
                        fullWidth
                        value={joinFormData.participant_phone}
                        onChange={(e) => setJoinFormData({ ...joinFormData, participant_phone: e.target.value })}
                        disabled={joining}
                        placeholder="Ej: +16073233248 o 6073233248"
                        helperText="Usa el mismo teléfono de tu cuenta Petflix si no tienes email"
                      />

                      {/* Carga de archivo desde dispositivo */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                          {joinDialogData.mediaRules
                            ? getParticipantMediaLabel(joinDialogData.mediaRules)
                            : 'Foto o video de tu mascota'}
                        </Typography>
                        <input
                          ref={mediaInputRef}
                          type="file"
                          accept={joinDialogData.mediaRules ? getMediaFileAcceptValue(joinDialogData.mediaRules) : 'image/*,video/*'}
                          capture={joinDialogData.mediaRules?.allowed_media_types === 'video' ? undefined : 'environment'}
                          style={{ display: 'none' }}
                          onChange={handleMediaFileSelect}
                          disabled={joining || uploadingMedia}
                        />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            startIcon={<Upload className="w-4 h-4" />}
                            onClick={() => mediaInputRef.current?.click()}
                            disabled={joining || uploadingMedia}
                            sx={{ textTransform: 'none' }}
                          >
                            {mediaFile ? 'Cambiar archivo' : 'Seleccionar archivo'}
                          </Button>
                          {mediaFile && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                                {mediaFile.name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={clearSelectedMedia}
                                disabled={joining || uploadingMedia}
                                aria-label="Quitar archivo"
                              >
                                <X className="w-4 h-4" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Sube 1 archivo desde tu computador o móvil · {joinDialogData.mediaRules ? formatMediaRulesSummary(joinDialogData.mediaRules) : 'formatos según reglas del concurso'}
                        </Typography>
                        {(uploadingMedia) && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress />
                            <Typography variant="caption" color="text.secondary">Subiendo archivo...</Typography>
                          </Box>
                        )}
                      </Box>

                      {mediaPreviewUrl && (
                        <Box sx={{
                          mb: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          border: '1px solid #e5e7eb',
                          borderRadius: 1,
                          p: 2,
                          backgroundColor: '#f9fafb'
                        }}>
                          {mediaPreviewType === 'video' ? (
                            <Box
                              component="video"
                              src={mediaPreviewUrl}
                              controls
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={mediaPreviewUrl}
                              alt="Vista previa"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                objectFit: 'contain',
                                borderRadius: 1,
                              }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Coupon Code Section - Solo para CouponPass */}
                      {selectedEntryType === 'coupon' && (
                        <Box sx={{ 
                          border: '1px solid #e5e7eb', 
                          borderRadius: 1, 
                          p: 2, 
                          backgroundColor: '#f9fafb',
                          mt: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Ticket className="w-5 h-5" style={{ color: '#9333ea' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#111827' }}>
                              Código de cupón o pase
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              label="Código de cupón o pase"
                              required
                              fullWidth
                              value={joinFormData.coupon_code}
                              onChange={(e) => {
                                setJoinFormData({ ...joinFormData, coupon_code: e.target.value });
                                setCouponValidation(null);
                              }}
                              disabled={joining || validatingCoupon}
                              placeholder="Ingresa tu código de cupón o pase"
                              sx={{ flexGrow: 1 }}
                            />
                            <Button
                              variant="outlined"
                              onClick={handleValidateCoupon}
                              disabled={joining || validatingCoupon || !joinFormData.coupon_code.trim()}
                              sx={{
                                borderColor: '#9333ea',
                                color: '#9333ea',
                                '&:hover': {
                                  borderColor: '#7e22ce',
                                  backgroundColor: '#f3e8ff'
                                }
                              }}
                            >
                              {validatingCoupon ? 'Validando...' : 'Validar'}
                            </Button>
                          </Box>

                          {/* Coupon Validation Result */}
                          {couponValidation && (
                            <Box sx={{ mt: 1 }}>
                              {couponValidation.valid ? (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1, 
                                  p: 1.5, 
                                  borderRadius: 1, 
                                  backgroundColor: '#d1fae5',
                                  border: '1px solid #10b981'
                                }}>
                                  <CheckCircle className="w-5 h-5" style={{ color: '#059669' }} />
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#065f46' }}>
                                      {couponValidation.kind === 'pass' ? 'Pase válido' : 'Cupón válido'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#047857' }}>
                                      Usos restantes: {couponValidation.uses_remaining} / {couponValidation.max_uses}
                                      {couponValidation.coupon_pass_name && ` • ${couponValidation.coupon_pass_name}`}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1, 
                                  p: 1.5, 
                                  borderRadius: 1, 
                                  backgroundColor: '#fee2e2',
                                  border: '1px solid #ef4444'
                                }}>
                                  <XCircle className="w-5 h-5" style={{ color: '#dc2626' }} />
                                  <Typography variant="body2" sx={{ color: '#991b1b' }}>
                                    {couponValidation.error || 'Código de cupón inválido'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Método de Pago - Solo para Entrada Normal */}
                      {selectedEntryType === 'normal' && (
                        <TextField
                          label="Método de Pago"
                          fullWidth
                          value={joinFormData.payment_method}
                          onChange={(e) => setJoinFormData({ ...joinFormData, payment_method: e.target.value })}
                          disabled={joining}
                          placeholder="Ej: Tarjeta de crédito, PayPal, etc."
                        />
                      )}

                      {/* Formulario PetFlix */}
                      {selectedEntryType === 'petflix' && (
                        <Box sx={{ 
                          border: '1px solid #e5e7eb', 
                          borderRadius: 1, 
                          p: 2, 
                          backgroundColor: '#f0fdf4',
                          mt: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Users className="w-5 h-5" style={{ color: '#10b981' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#111827' }}>
                              Datos de PetFlix
                            </Typography>
                          </Box>
                          
                          <TextField
                            label="ID de Usuario PetFlix"
                            required
                            fullWidth
                            value={petflixFormData.user_id}
                            onChange={(e) => setPetflixFormData({ ...petflixFormData, user_id: e.target.value })}
                            disabled={joining}
                            placeholder="Ingresa el ID del usuario de PetFlix"
                            sx={{ mb: 2 }}
                          />
                          
                          <TextField
                            label="ID del Post Social"
                            required
                            fullWidth
                            value={petflixFormData.post_social_id}
                            onChange={(e) => setPetflixFormData({ ...petflixFormData, post_social_id: e.target.value })}
                            disabled={joining}
                            placeholder="Ingresa el ID del post social de PetFlix"
                            sx={{ mb: 2 }}
                          />
                          
                          <TextField
                            label="Código de Cupón (Opcional)"
                            fullWidth
                            value={petflixFormData.coupon_code}
                            onChange={(e) => setPetflixFormData({ ...petflixFormData, coupon_code: e.target.value })}
                            disabled={joining}
                            placeholder="Ingresa el código de cupón si tienes uno"
                          />
                          
                          <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
                            El sistema obtendrá automáticamente los datos del usuario y del post desde PetFlix.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button 
              onClick={() => {
                setJoinDialogOpen(false);
                setSelectedEntryType(null);
                setCouponValidation(null);
                clearSelectedMedia();
                setPetflixFormData({
                  user_id: '',
                  post_social_id: '',
                  coupon_code: ''
                });
              }}
              disabled={joining || uploadingMedia}
              variant="outlined"
            >
              {selectedEntryType ? 'Atrás' : 'Cancelar'}
            </Button>
            {selectedEntryType && (
              <Button
                onClick={handleJoinContest}
                disabled={
                  joining ||
                  uploadingMedia ||
                  (selectedEntryType === 'normal' && !joinFormData.participant_name.trim()) ||
                  (selectedEntryType === 'coupon' && (!joinFormData.participant_name.trim() || !couponValidation || !couponValidation.valid)) ||
                  (selectedEntryType === 'petflix' && (!petflixFormData.user_id.trim() || !petflixFormData.post_social_id.trim()))
                }
                variant="contained"
                sx={{
                  backgroundColor: selectedEntryType === 'petflix' ? '#10b981' : '#2563eb',
                  '&:hover': { 
                    backgroundColor: selectedEntryType === 'petflix' ? '#059669' : '#1d4ed8' 
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                    opacity: 0.6
                  }
                }}
              >
                {joining || uploadingMedia ? (uploadingMedia ? 'Subiendo archivo...' : 'Uniéndose...') : 'Confirmar y Unirse'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Join by Name Dialog */}
        <Dialog 
          open={joinByNameDialogOpen} 
          onClose={() => {
            if (!joiningByName) {
              setJoinByNameDialogOpen(false);
              setJoinByNameError('');
              setJoinByNameSuccess('');
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Trophy className="w-5 h-5" style={{ color: '#2563eb' }} />
              Unirse por Nombre de Concurso
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {joinByNameError && (
                <Alert severity="error" onClose={() => setJoinByNameError('')}>
                  {joinByNameError}
                </Alert>
              )}
              
              {joinByNameSuccess && (
                <Alert severity="success" onClose={() => setJoinByNameSuccess('')}>
                  {joinByNameSuccess}
                </Alert>
              )}

              <TextField
                label="Nombre del Concurso"
                required
                fullWidth
                value={joinByNameFormData.contest_name}
                onChange={(e) => setJoinByNameFormData({ ...joinByNameFormData, contest_name: e.target.value })}
                disabled={joiningByName}
                placeholder="Ingresa el nombre exacto del concurso"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563eb',
                    },
                  },
                }}
              />
              
              <TextField
                label="ID de Usuario"
                required
                fullWidth
                value={joinByNameFormData.user_id}
                onChange={(e) => setJoinByNameFormData({ ...joinByNameFormData, user_id: e.target.value })}
                disabled={joiningByName}
                placeholder="Ingresa el ID del usuario"
              />
              
              <TextField
                label="ID del Post Social"
                required
                fullWidth
                value={joinByNameFormData.post_social_id}
                onChange={(e) => setJoinByNameFormData({ ...joinByNameFormData, post_social_id: e.target.value })}
                disabled={joiningByName}
                placeholder="Ingresa el ID del post social"
              />
              
              <TextField
                label="Código de Cupón (Opcional)"
                fullWidth
                value={joinByNameFormData.coupon_code}
                onChange={(e) => setJoinByNameFormData({ ...joinByNameFormData, coupon_code: e.target.value })}
                disabled={joiningByName}
                placeholder="Ingresa el código de cupón si tienes uno"
              />
              
              <TextField
                label="Clave de Autenticación (PET_CONTEST_ACCESS_KEY)"
                required
                fullWidth
                type="password"
                value={joinByNameFormData.access_key}
                onChange={(e) => setJoinByNameFormData({ ...joinByNameFormData, access_key: e.target.value })}
                disabled={joiningByName}
                placeholder="Ingresa la clave de autenticación PET_CONTEST_ACCESS_KEY"
                helperText="Requerida para autenticar la petición. Si está configurada en .env, este campo es opcional."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563eb',
                    },
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button 
              onClick={() => {
                setJoinByNameDialogOpen(false);
                setJoinByNameError('');
                setJoinByNameSuccess('');
              }}
              disabled={joiningByName}
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleJoinByName}
              disabled={
                joiningByName || 
                !joinByNameFormData.contest_name.trim() || 
                !joinByNameFormData.user_id.trim() || 
                !joinByNameFormData.post_social_id.trim() ||
                (!joinByNameFormData.access_key.trim() && !environment.petContestAccessKey)
              }
              variant="contained"
              sx={{
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' },
                '&:disabled': {
                  backgroundColor: '#9ca3af',
                  opacity: 0.6
                }
              }}
            >
              {joiningByName ? 'Enviando...' : 'Unirse al Concurso'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ContestParticipantPage;

